import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Icons } from "../lib/icons";
import { Btn, Pill } from "../lib/primitives";
import { SaveBar } from "./SaveBar";
import { apiClient, ApiError } from "../lib/api";
import { useOperatorRole } from "../lib/hooks";

const MAX_ORIGINS = 20;
const MAX_ORIGIN_LENGTH = 255;

/** The callback allow-list, saved as soon as an address is added or removed. */
export function CallbackOriginsCard() {
  const queryClient = useQueryClient();
  // Viewers get a read-only card (#52); the server still enforces RBAC.
  const { canWrite } = useOperatorRole();
  const { data, isLoading } = useQuery({
    queryKey: ["hosted"],
    queryFn: () => apiClient.hosted.get(),
    staleTime: 60_000,
  });

  // `draft` is the working list — null while we don't have a baseline yet.
  const [draft, setDraft] = useState<string[] | null>(null);
  const [input, setInput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (data && draft === null) setDraft(data.allowedCallbackOrigins);
  }, [data, draft]);

  const updateMutation = useMutation({
    mutationFn: (origins: string[]) =>
      apiClient.hosted.update({ allowedCallbackOrigins: origins }),
    onSuccess: (saved) => {
      setError(null);
      setSuccess("Saved.");
      setDraft(saved.allowedCallbackOrigins);
      queryClient.setQueryData(["hosted"], saved);
      window.setTimeout(() => setSuccess(null), 2000);
    },
    onError: (err) => {
      // Revert the optimistic add/remove back to the last server-confirmed list.
      setDraft(data?.allowedCallbackOrigins ?? []);
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Failed to save");
    },
  });

  if (isLoading || draft === null) {
    return (
      <div className="ad-card">
        <div className="ad-card-head"><div className="ad-card-title">Callback URLs</div></div>
        <div style={{ fontSize: 12, color: "var(--ad-fg-3)" }}>Loading…</div>
      </div>
    );
  }

  const baseline = data?.allowedCallbackOrigins ?? [];
  const dirty =
    draft.length !== baseline.length ||
    draft.some((o, i) => o !== baseline[i]);

  function onAdd() {
    setError(null);
    const trimmed = input.trim();
    if (!trimmed) return;
    if (draft!.length >= MAX_ORIGINS) {
      setError(`Up to ${MAX_ORIGINS} addresses.`);
      return;
    }
    if (trimmed.length > MAX_ORIGIN_LENGTH) {
      setError(`Keep it under ${MAX_ORIGIN_LENGTH} characters.`);
      return;
    }
    // Client-side normalise so the user sees the canonical form immediately.
    // The server re-validates and is the source of truth — this is just UX.
    let canonical = trimmed;
    try {
      canonical = new URL(trimmed).origin;
    } catch {
      setError(`That doesn't look like a web address: ${trimmed}`);
      return;
    }
    if (draft!.includes(canonical)) {
      setError("That address is already on the list.");
      return;
    }
    // Add + persist immediately so there's no separate "Save" step to forget.
    const next = [...draft!, canonical];
    setDraft(next);
    setInput("");
    updateMutation.mutate(next);
  }

  function onRemove(origin: string) {
    setError(null);
    // Remove + persist immediately (mirrors add).
    const next = draft!.filter((o) => o !== origin);
    setDraft(next);
    updateMutation.mutate(next);
  }

  function onSave() {
    if (!dirty || updateMutation.isPending) return;
    updateMutation.mutate(draft!);
  }

  function onDiscard() {
    setError(null);
    setDraft(baseline);
    setInput("");
  }

  return (
    <div className="ad-card">
      <div className="ad-card-head">
        <div>
          <div className="ad-card-title">Callback URLs</div>
          <div style={{ fontSize: 14, color: "var(--ad-fg-3)", marginTop: 4 }}>
            Where people go back to after signing in. Anything not on this list is refused.
          </div>
        </div>
        <Pill kind={draft.length === 0 ? "warn" : "success"} dot>
          {draft.length === 0 ? "Any address" : `${draft.length} allowed`}
        </Pill>
      </div>

      {draft.length === 0 && (
        <div
          style={{
            fontSize: 13,
            color: "var(--ad-fg-2)",
            padding: "10px 12px",
            background: "var(--ad-surface-2)",
            borderRadius: 12,
            marginBottom: 10,
          }}
        >
          With an empty list any address is accepted. Add your app's address before you go live.
        </div>
      )}

      <div className="ad-stack-sm" style={{ marginBottom: 10 }}>
        {draft.map((o) => (
          <div
            key={o}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 8px 6px 10px",
              background: "var(--ad-surface-2)",
              borderRadius: 10,
              fontFamily: "var(--rb-font-mono)",
              fontSize: 13,
            }}
          >
            <span style={{ flex: 1, color: "var(--ad-fg-1)", wordBreak: "break-all" }}>{o}</span>
            {canWrite && (
              <button
                type="button"
                className="ad-btn ad-btn-ghost"
                onClick={() => onRemove(o)}
                style={{ padding: "2px 6px", fontSize: 11 }}
                aria-label={`Remove ${o}`}
              >
                <Icons.X size={12} />
              </button>
            )}
          </div>
        ))}
      </div>

      {canWrite && (
        <div className="ad-field">
          <div style={{ display: "flex", gap: 6 }}>
            <input
              className="ad-input ad-input-mono"
              placeholder="https://app.acme.com"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onAdd();
                }
              }}
              maxLength={MAX_ORIGIN_LENGTH}
              style={{ flex: 1 }}
            />
            <Btn kind="ghost" leading={<Icons.Plus size={12} />} onClick={onAdd}>Add</Btn>
          </div>
          <div className="ad-field-hint">
            Just the address, no path. HTTPS only, except http://localhost while you build. Up to {MAX_ORIGINS}.
          </div>
        </div>
      )}

      {error && (
        <div
          style={{
            marginTop: 8,
            padding: 8,
            borderRadius: 6,
            fontSize: 12,
            background: "rgba(220, 38, 38, 0.08)",
            color: "#b91c1c",
          }}
        >
          {error}
        </div>
      )}

      {/* Save/Discard moved to the contextual bottom SaveBar. */}
      {canWrite && success && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
          <span style={{ fontSize: 11, color: "var(--ad-fg-3)" }}>{success}</span>
        </div>
      )}

      <SaveBar
        visible={canWrite && dirty}
        pending={updateMutation.isPending}
        onDiscard={onDiscard}
        onPublish={onSave}
        label="Save"
        pendingLabel="Saving…"
      />
    </div>
  );
}

